import React from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Route-level error boundary. Without one, any render-time error on a page
 * unmounts the whole React tree and the user sees a white screen until a hard
 * reload (the symptom we hit when navigating during a deploy). This catches it,
 * shows a recoverable card in the content area (sidebar/topbar stay), and resets
 * automatically when the route changes - so navigating away recovers on its own.
 */
class ErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep a console trace so a transient render/chunk error is debuggable.
    // eslint-disable-next-line no-console
    console.error('[RouteErrorBoundary]', error, info?.componentStack);

    // Ship the crash to the API (fire-and-forget) so render errors are visible
    // in server logs - a user saying "the page hit a snag" is otherwise
    // undebuggable without their browser console.
    try {
      fetch(`${process.env.REACT_APP_API_URL || ''}/api/client-errors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: String(error?.message || error).slice(0, 500),
          stack: String(error?.stack || '').slice(0, 2000),
          component_stack: String(info?.componentStack || '').slice(0, 2000),
          path: window.location.pathname,
          ua: navigator.userAgent,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch (_) { /* reporting must never throw */ }

    // Most snags here are transient - a tab running a STALE bundle right after a
    // deploy (we ship several times a day). Self-heal by reloading ONCE to pull
    // the fresh app. Guard is TIME-BASED and global (not per-route): if we
    // already auto-reloaded in the last 30s, the reload clearly didn't fix it,
    // so we show the recoverable card instead of reloading again. This makes an
    // infinite reload loop impossible even when a lazy chunk keeps failing
    // (its async error fires AFTER mount, which a per-route marker couldn't
    // survive).
    try {
      const KEY = 'mm-eb-last-reload';
      const last = Number(sessionStorage.getItem(KEY) || 0);
      const now = Date.now();
      if (!last || now - last > 30000) {
        sessionStorage.setItem(KEY, String(now));
        window.location.reload();
      }
    } catch (_) { /* sessionStorage blocked - just show the card */ }
  }

  componentDidUpdate(prevProps) {
    // Route changed -> clear the error so the next page renders fresh.
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex h-full min-h-[60vh] flex-col items-center justify-center gap-4 p-10 text-center">
          <AlertTriangle className="size-8 text-amber-500" />
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">This page hit a snag</h2>
            <p className="max-w-md text-sm text-muted-foreground">
              Something failed while rendering this page. This is usually temporary -
              for example right after an app update. Try again, or reload.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => this.setState({ error: null })}>
              <RotateCcw className="size-4" /> Try again
            </Button>
            <Button onClick={() => window.location.reload()}>Reload page</Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundaryInner resetKey={location.pathname}>{children}</ErrorBoundaryInner>;
}
